import { StateBlock } from "@/components/layout-2/ds";
import { InlineAlert, ObjectChoice } from "@/components/layout-2/ds-settings";
import { FilterTreeCount, useFilterTreeDraft } from "@/components/layout-2/filter-tree/filter-tree-dialog";
import { FilterTreeEditor } from "@/components/layout-2/filter-tree/tree-editor";
import { areTreesEqual } from "@/components/layout-2/filter-tree/tree-utils";
import type { FilterTree } from "@/components/layout-2/filter-tree/types";
import { ObjectListFilters2 } from "@/components/layout-2/object-list-filters-2";
import type { Filter } from "@/components/layout/types";
import { Button } from "@/components/ui/button";
import { useObjectListFilters } from "@/hooks/use-object-list-filters";
import { DataService } from "@/services/data";
import { formatParis } from "@/utils/dialer-datetime";
import { DRQBuilder } from "@/utils/drq";
import { useDebounce } from "@/utils/misc";
import { ChevronLeft, Undo2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { errorDetail, feedingClient } from "./api";
import { formatCount } from "./counts";
import { configBody, listViewChanges, NO_DRAFT, type DraftChange, type NewView } from "./draft";
import { describeFilters, describeTree, useFilterDefinitions } from "./filter-chips";
import { ListViewPicker, type ListViewOption } from "./list-view-picker";
import { StepDialog } from "./step-dialog";
import type { StepProps } from "./steps";
import type { DRQ, FeedingObject } from "./types";

/** `filter_tree`: advanced filters, compiled into `filters` by the server on Save. */
export type FiltersValue = { filters: DRQ; filter_logic: string | null; filter_tree?: FilterTree | null };

const EMPTY: FiltersValue = { filters: {}, filter_logic: null, filter_tree: null };

/** Where the filters being edited come from: the saved source, a standard ListView, or nothing. */
type Start = { kind: "current" } | { kind: "view"; view: ListViewOption } | { kind: "empty" };

/** What the editor hands to the step at every change. */
type EditorState = { preview: FiltersValue; unchanged: boolean; invalid: boolean; empty: boolean };
type EditorHandle = { value: () => FiltersValue | null };

/**
 * Step 1: the records to call — the object, and the filters a record must match to become a card. The
 * campaign keeps its own copy of a ListView: starting from a standard one only loads its filters here, to
 * adjust before saving (the copy and the adjusted filters go in one Save). Examples of the matching records
 * follow every change.
 */
export function RecordsStep({ page, send, context, onClose, onDone }: StepProps) {
    const { source } = page;
    const current = source.list_view;
    const [objectType, setObjectType] = useState(source.object_type);
    const [start, setStart] = useState<Start>(current ? { kind: "current" } : { kind: "empty" });
    const [picking, setPicking] = useState(false);
    const [picked, setPicked] = useState<ListViewOption | null>(null);
    const [pickedEmpty, setPickedEmpty] = useState(false);
    const [loaded, setLoaded] = useState<string | null>(null);
    const [state, setState] = useState<EditorState>({ preview: EMPTY, unchanged: true, invalid: false, empty: true });
    const handle = useRef<EditorHandle | null>(null);
    const listed = source.objects.find(item => item.type === objectType) ?? source.objects[0];
    // The source's own object meta when it is on this object (it names the model the advanced editor needs).
    const object = current && current.object.type === objectType ? { ...listed, ...current.object } : listed;
    const definitions = useFilterDefinitions(object.url);
    const objectMoved = objectType !== source.object_type;

    const initial: FiltersValue =
        start.kind === "current" && current
            ? { filters: current.filters, filter_logic: current.filter_logic, filter_tree: current.filter_tree ?? null }
            : start.kind === "view"
              ? { filters: start.view.filters ?? {}, filter_logic: start.view.filter_logic ?? null, filter_tree: start.view.filter_tree ?? null }
              : EMPTY;
    const editorKey = `${objectType}:${start.kind === "view" ? start.view.id : start.kind}`;

    const chooseObject = (type: string) => {
        setObjectType(type);
        setLoaded(null);
        // The saved source stays only on its own object; another object starts empty.
        setStart(type === source.object_type && current ? { kind: "current" } : { kind: "empty" });
    };
    const usePicked = () => {
        if (pickedEmpty) {
            setStart({ kind: "empty" });
            setLoaded("No filter");
        } else if (picked) {
            setStart({ kind: "view", view: picked });
            setLoaded(picked.name);
        }
        setPicking(false);
    };

    const newSource: NewView | null =
        start.kind === "view"
            ? { key: `source-${start.view.id}`, name: start.view.name, object, records: null, spec: { mode: "duplicate", source_list_view_id: start.view.id } }
            : start.kind === "empty" && (!current || objectMoved || loaded)
              ? { key: "new-source", name: "Source", object, records: null, spec: { mode: "create", name: "Source" } }
              : null;
    const dirty = newSource !== null || !state.unchanged;

    const prepare = () => {
        const value = handle.current?.value();
        if (!value) return null;
        const config = !page.config.exists ? configBody(page, { ...page.config.values }, objectType) : objectMoved ? { object_type: objectType } : null;
        if (!newSource) {
            return { body: { source_filters: value }, changes: listViewChanges(page, { ...NO_DRAFT, filters: value }, definitions, current?.filter_logic ?? "") };
        }
        // A copy keeps its template's filters unless told otherwise: its filters always go with it, even none.
        const sendFilters = newSource.spec.mode === "duplicate" || !!value.filter_tree || Object.keys(value.filters ?? {}).length > 0;
        const changes: DraftChange[] = [
            ...listViewChanges(page, { ...NO_DRAFT, source: newSource }, [], ""),
            { kind: "changed", label: "Filters", after: describe(value, definitions) || "No filter" }
        ];
        return { body: { ...(config ? { config } : {}), source: newSource.spec, ...(sendFilters ? { source_filters: value } : {}) }, changes };
    };

    const origin =
        start.kind === "current" && current
            ? current.origin.kind === "copy"
                ? `Started from “${current.origin.from_name}” on ${formatParis(current.origin.at, "dd/MM")}`
                : "Created for this campaign"
            : start.kind === "view"
              ? `From “${start.view.name}” · not saved yet`
              : "No filter yet: add one, or start from a ListView.";

    const pickerFooter = (
        <>
            <Button type="button" variant="ghost" onClick={() => setPicking(false)} data-testid="picker-back">
                <ChevronLeft className="mr-1 h-4 w-4" /> Back to the filters
            </Button>
            <Button type="button" disabled={!picked && !pickedEmpty} onClick={usePicked} data-testid="picker-use">
                Use these filters
            </Button>
        </>
    );

    return (
        <StepDialog
            title={picking ? "Start from a ListView" : "Records to call"}
            description={
                picking ? "Its filters are copied into the editor, to adjust before saving. The ListView itself is not changed." : "The object, and the filters a record must match to become a card."
            }
            size="xl"
            context={context}
            page={page}
            send={send}
            onClose={onClose}
            onDone={onDone}
            prepare={prepare}
            dirty={dirty && !state.invalid}
            canSkip={!!current}
            footer={picking ? pickerFooter : undefined}
            testId="records-dialog"
        >
            {picking && (
                <ListViewPicker
                    campaignCode={page.campaign.code}
                    objects={source.objects}
                    objectType={objectType}
                    extra={{ value: "empty", label: "No filter", description: "Start from an empty set of filters" }}
                    onExtra={() => setPickedEmpty(true)}
                    onChange={view => {
                        setPicked(view);
                        if (view) setPickedEmpty(false);
                    }}
                />
            )}
            {/* The editor stays mounted under the picker: Back finds it as it was. */}
            <div className={picking ? "hidden" : "flex flex-col gap-4"}>
                <div className="flex flex-col gap-1.5">
                    <span className="text-xs text-gray-600">Object</span>
                    <ObjectChoice
                        label="Object"
                        value={objectType}
                        onChange={chooseObject}
                        items={source.objects.map(item => ({ value: item.type, label: item.label, icon: item.icon, color: item.color }))}
                    />
                    {current && objectMoved && <p className="text-xs text-gray-600">Another object means other records: the current filters are replaced.</p>}
                </div>

                <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-3">
                    <div className="flex flex-col">
                        <span className="text-sm font-semibold text-black">Filters</span>
                        <span className="text-xs text-gray-500" data-testid="records-origin">
                            {origin}
                        </span>
                    </div>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                            setPicked(null);
                            setPickedEmpty(false);
                            setPicking(true);
                        }}
                        data-testid="start-from"
                    >
                        Start from a ListView…
                    </Button>
                </div>

                {loaded && (
                    <InlineAlert type="info">
                        <span data-testid="filters-loaded">{loaded === "No filter" ? "Filters cleared." : `Filters of “${loaded}” loaded.`} Adjust them, then save: nothing is saved yet.</span>
                    </InlineAlert>
                )}

                {/* The filters take the room; what they match stays a narrow summary beside them. */}
                <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_16rem]">
                    <FiltersEditor key={editorKey} object={object} definitions={definitions} initial={initial} handle={handle} onState={setState} />
                    <Sample campaignCode={page.campaign.code} object={object} preview={state.preview} limit={page.limits.max_source_records} invalid={state.invalid} />
                </div>

                {state.empty && (
                    <InlineAlert type="warning">
                        <span data-testid="no-filter-warning">No filter: every {object.label.toLowerCase()} record can become a card.</span>
                    </InlineAlert>
                )}
                {current && newSource && page.state !== "draft" && (
                    <InlineAlert type="warning">
                        <span data-testid="source-replace-warning">Saving replaces the campaign's ListView: the scheduled feeding stops until the campaign is put in service again.</span>
                    </InlineAlert>
                )}
            </div>
        </StepDialog>
    );
}

const describe = (value: FiltersValue, definitions: Filter[]) =>
    (value.filter_tree ? describeTree(value.filter_tree) : describeFilters(value.filters, definitions)).map(chip => [chip.label, chip.value].filter(Boolean).join(" ")).join(" · ");

/**
 * The editor of the standard list pages, fed with the object's definitions: the side panel, or the
 * advanced editor in place (no second dialog). Mounted per starting point, so a new start resets it.
 */
function FiltersEditor({
    object,
    definitions,
    initial,
    handle,
    onState
}: {
    object: FeedingObject;
    definitions: Filter[];
    initial: FiltersValue;
    handle: React.MutableRefObject<EditorHandle | null>;
    onState: (state: EditorState) => void;
}) {
    const editor = useObjectListFilters();
    useEffect(() => {
        editor.loadFiltersFromDRQ(initial.filters ?? {}, true);
        editor.handleSetFilterLogic(initial.filter_logic ?? "");
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const rootModel = object.model;
    const [startTree, setStartTree] = useState<FilterTree | null>(rootModel ? (initial.filter_tree ?? null) : null);
    const advanced = startTree !== null;
    const tree = useFilterTreeDraft({ active: advanced, tree: startTree, countUrl: `/crm/objects/${object.url}/count/` });

    const draft = editor.drqFilterState;
    const filters = useMemo(() => DRQBuilder.buildFromFilterInstances(draft), [draft]);
    const filterLogic = editor.filterLogic || null;
    const unchanged = advanced
        ? areTreesEqual(tree.pruned, initial.filter_tree)
        : !initial.filter_tree && JSON.stringify(filters) === JSON.stringify(initial.filters ?? {}) && (filterLogic ?? "") === (initial.filter_logic ?? "");
    const empty = advanced ? tree.pruned.root.children.length === 0 : draft.length === 0;
    const invalid = advanced && tree.countState.status === "error";
    const preview: FiltersValue = advanced ? (empty ? EMPTY : { filters: {}, filter_logic: null, filter_tree: tree.pruned }) : { filters, filter_logic: filterLogic, filter_tree: null };
    const signature = JSON.stringify(preview);

    useEffect(() => onState({ preview, unchanged, invalid, empty }), [signature, unchanged, invalid, empty]); // eslint-disable-line react-hooks/exhaustive-deps
    handle.current = {
        value: () => {
            if (!advanced) return { filters, filter_logic: filterLogic, filter_tree: null };
            const next = tree.validate();
            if (!next) return null;
            return next.root.children.length ? { filters: {}, filter_logic: null, filter_tree: next } : EMPTY;
        }
    };

    // Switches to the advanced editor on the side panel's filters: read back by the server.
    const openAdvanced = async (panel?: { filters: typeof draft; logic: string }) => {
        if (!rootModel) return;
        try {
            const next = await DataService.client
                .url("/crm/app/filter-tree/from-filters/")
                .post({ model: rootModel, filters: panel ? DRQBuilder.buildFromFilterInstances(panel.filters) : filters, filter_logic: (panel ? panel.logic : filterLogic) ?? "" })
                .json<FilterTree>();
            setStartTree(next);
        } catch (error) {
            console.error("Error reading the filters for the advanced editor:", error);
            toast.error("These filters cannot be opened in the advanced editor");
        }
    };
    // A tree does not always read back as flat filters: going back starts the side panel empty.
    const backToSimple = () => {
        setStartTree(null);
        editor.handleClearAllFilters();
    };

    if (advanced && rootModel) {
        return (
            <div className="flex flex-col gap-2" data-testid="advanced-filters">
                <div className="flex items-center justify-between">
                    <h3 className="font-semibold">Advanced filters</h3>
                    <button type="button" className="text-sm text-blue-700 hover:underline" onClick={backToSimple}>
                        <Undo2 className="mr-1 inline-block" size={14} />
                        Back to simple filters (clears them)
                    </button>
                </div>
                <FilterTreeEditor rootModel={rootModel} root={tree.draft.root} update={tree.update} append={tree.append} errors={tree.errors} focusId={tree.focusId} />
                <FilterTreeCount countState={tree.countState} incomplete={tree.incomplete} />
            </div>
        );
    }
    return (
        <div data-testid="filters-editor">
            <ObjectListFilters2
                title="Conditions"
                searchButtonText="Apply filters"
                filters={definitions}
                filterState={draft}
                currentLogic={editor.filterLogic}
                onSetLogic={editor.handleSetFilterLogic}
                onAddFilter={editor.handleAddFilter}
                onUpdateFilter={editor.handleUpdateFilter}
                onRemoveFilter={editor.handleRemoveFilter}
                onClearAllFilters={editor.handleClearAllFilters}
                rootModel={rootModel}
                onOpenAdvanced={rootModel ? openAdvanced : undefined}
            />
        </div>
    );
}

type SampleResult = {
    records: number;
    records_limit: number;
    order: { label: string; field: string };
    examples: Array<{ id: number; name: string; href: string; account: string | null; contact: string | null; ordered_on: string | null }>;
};

/** How many records the filters being edited match, and the first ones a pass would read: recomputed as they change. */
/** How many matching records the step shows: enough to recognise them, no more. */
const SAMPLE_EXAMPLES = 3;

function Sample({ campaignCode, object, preview, limit, invalid }: { campaignCode: string; object: FeedingObject; preview: FiltersValue; limit: number; invalid: boolean }) {
    const signature = useDebounce(JSON.stringify({ object: object.type, preview }), 600);
    const [result, setResult] = useState<{ data: SampleResult | null; error: string | null; loading: boolean }>({ data: null, error: null, loading: true });

    useEffect(() => {
        if (invalid) return;
        let cancelled = false;
        setResult(previous => ({ ...previous, loading: true }));
        const { preview: value } = JSON.parse(signature) as { preview: FiltersValue };
        feedingClient(campaignCode, "sample/")
            .post({ object_type: object.type, ...value })
            .json<SampleResult>()
            .then(data => !cancelled && setResult({ data, error: null, loading: false }))
            .catch(async error => {
                const message = await errorDetail(error, "The examples could not be computed.");
                if (!cancelled) setResult({ data: null, error: message, loading: false });
            });
        return () => {
            cancelled = true;
        };
    }, [signature, invalid, campaignCode]); // eslint-disable-line react-hooks/exhaustive-deps

    const { data, error, loading } = result;
    const examples = data?.examples?.slice(0, SAMPLE_EXAMPLES) ?? [];
    return (
        <div className="flex min-w-0 flex-col self-start rounded-md border text-sm" data-testid="records-sample">
            <div className="flex flex-col gap-0.5 px-3 py-2">
                <span className="flex items-baseline justify-between gap-2">
                    <span className="text-xs text-gray-600">Matching now</span>
                    <span className="text-lg font-bold tabular-nums text-black" data-testid="sample-count">
                        {typeof data?.records === "number" ? formatCount(data.records, limit) : "—"}
                    </span>
                </span>
                <span className="text-xs text-gray-500">
                    {object.label.toLowerCase()} records, before rules and exclusions{loading ? " · counting…" : ""}
                </span>
            </div>
            {error ? (
                <p className="border-t px-3 py-2 text-xs text-gray-600" data-testid="sample-error">
                    {error}
                </p>
            ) : !data ? (
                <StateBlock kind="loading" className="py-3" />
            ) : !examples.length ? (
                <p className="border-t px-3 py-2 text-xs text-gray-600">No record matches these filters.</p>
            ) : (
                <ul className="flex flex-col border-t" data-testid="sample-examples" aria-label={`Examples, ${data.order.label.toLowerCase()}`}>
                    {examples.map(example => (
                        <li key={example.id} className="flex min-w-0 flex-col border-b px-3 py-1.5 last:border-b-0">
                            <Link to={`/${example.href.replace(/^\//, "")}`} target="_blank" className="truncate text-blue-700 hover:text-blue-900 hover:underline">
                                {example.name}
                            </Link>
                            <span className="truncate text-xs text-gray-500">
                                {[example.account, example.ordered_on ? formatParis(example.ordered_on, "dd/MM/yyyy") : null].filter(Boolean).join(" · ")}
                            </span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
